import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Tabs from "../../Components/Tabs";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { fetchAdminPartners } from "../../Redux/AdminPartners/AdminPartnersActions";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import { CLIENTS_CATEGORIES, USER_TYPES } from "../../constants/globals";
import { absoluteUrl } from "../../_helper";
import CustomPagination from "../../Components/CustomPagination";
import PageTitle from "../../Components/UI/PageTitle";
import RowLoader from "../../Components/UI/RowLoader";
import Flatpickr from "react-flatpickr";
import DropdownSelect from "../../Components/DropdownSelect";
import EyeDetailsIcon from "../../Components/Svgs/EyeDetailsIcon";
import EditIcon from "../../Components/Svgs/EditIcon";
import {
  deleteUncompletedPartner,
  getTabsTotalNumbersForAdmin,
} from "../../apis";
import DeleteIcon from "../../Components/Svgs/DeleteIcon";
import Swal from "sweetalert2";

const AdminPartnersList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [viewTabsTotalNumbers, setViewTabsTotalNumbers] = useState({});

  const [filterByClientCategory, setFilterByClientCategory] = useState("");
  const [filterByPvActive, setFilterByPvActive] = useState("");
  const [filterByActivationStatus, setFilterByActivationStatus] = useState("");

  const [currentTab, setCurrentTab] = useState(
    new URLSearchParams(location.search).get("tab") || "all"
  );

  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );

  const { adminPartnersData, isLoading, pagination } = useSelector(
    (state) => state.AdminPartners
  );
  const { loggedInUser } = useSelector((state) => state.Auth);
  const currentUserType = loggedInUser?.data?.model?.user_type_id;

  const DATA_TABS = [
    {
      key: "all",
      label: "All Partners",
      number: viewTabsTotalNumbers.allPartnersCnt,
    },
    {
      key: "pv-active",
      label: "Active",
      number: viewTabsTotalNumbers.activeCnt,
    },
    {
      key: "pending",
      label: "Pending",
      number: viewTabsTotalNumbers.pendingCnt,
    },
    {
      key: "uncompleted",
      label: "Uncompleted",
      number: viewTabsTotalNumbers.uncompletedCnt,
    },
  ];

  // get Total Numbers
  useEffect(() => {
    getTabsTotalNumbersForAdmin()
      .then((response) => {
        setViewTabsTotalNumbers(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    dispatch(
      fetchAdminPartners(
        currentTab,
        searchTerm,
        currentPage,
        sortKey,
        sortDirection,
        filterByClientCategory?.id,
        startDate,
        endDate,
        filterByPvActive?.id,
        filterByActivationStatus?.id
      )
    );
  }, [
    currentTab,
    searchTerm,
    currentPage,
    sortKey,
    sortDirection,
    dispatch,
    filterByClientCategory,
    startDate,
    endDate,
    filterByPvActive,
    filterByActivationStatus,
  ]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("tab", currentTab);
    if (searchTerm) params.set("search", searchTerm);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [currentTab, searchTerm, currentPage, sortKey, sortDirection, navigate]);

  const handleTabClick = (key) => {
    setCurrentTab(key);
    setFilterByClientCategory("");
    setCurrentPage(1); // Reset to first page on tab change
  };

  const handleFilter = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column, direction) => {
    setSortKey(column.sortKey);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const filterAndSortData = () => {
    return adminPartnersData;
  };

  const handleDeletePartner = (partnerId) => {
    if (!partnerId) return;

    Swal.fire({
      title: "Delete Partner",
      html: `
      <p>Are you sure you want to delete this partner?</p>
      <textarea id="note-input" class="swal2-textarea" placeholder="Write a reason for deletion..." required></textarea>
    `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      focusConfirm: false,
      showLoaderOnConfirm: true,
      preConfirm: () => {
        const note = document.getElementById("note-input").value.trim();

        if (!note) {
          Swal.showValidationMessage("Please enter a reason for deletion.");
          return;
        }

        return deleteUncompletedPartner(partnerId, note)
          .then((response) => response.data)
          .catch((error) => {
            console.error("Error deleting uncompleted partner:", error);
            Swal.showValidationMessage(
              error.response.data.message || "Failed to delete partner."
            );
          });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire(
          "Deleted!",
          "Uncompleted partner removed successfully.",
          "success"
        );

        // refetch data after deleting the partner
        dispatch(
          fetchAdminPartners(
            currentTab,
            searchTerm,
            currentPage,
            sortKey,
            sortDirection,
            filterByClientCategory?.id,
            startDate,
            endDate,
            filterByPvActive?.id,
            filterByActivationStatus?.id
          )
        );
      }
    });
  };

  const hiddenColumns = [
    "id",
    "action",
    "must_verify_email",
    "client_id",
    "can_be_deleted",
  ]; // Add column keys you want to hide

  const uncompletedTab = [
    {
      name: "Delete",
      selector: (row) => row.name,
      ui: (row) =>
        row.can_be_deleted === true ? (
          <button
            className="!tw-m-0 !tw-p-0"
            onClick={() => handleDeletePartner(row.id)}
          >
            <DeleteIcon width="17px" />
          </button>
        ) : (
          <>-</>
        ),
    },
  ];

  const customColumns =
    currentTab === "all" || currentTab === "pv-active"
      ? [
          {
            name: "Partner's Tree",
            selector: (row) => row.name + " - custom",
            ui: (row) => (
              <Link to={`/tree/${row.id}`}>
                <img
                  className="icon-color"
                  src={absoluteUrl("imgs/tree.png")}
                  style={{ width: 30 }}
                  alt=""
                />
              </Link>
            ),
          },
          {
            name: "Partner Network",
            selector: (row) => row.name + " - custom",
            ui: (row) => (
              <div className="tw-flex tw-gap-1">
                {currentUserType !== USER_TYPES.PARTNER && (
                  <Link
                    className="tw-text-primary"
                    to={`/partners-list/partner-network/${row.id}`}
                  >
                    <span className="material-icons tw-text-[30px]">group</span>
                  </Link>
                )}
              </div>
            ),
          },
          ...(currentTab === "all"
            ? [
                {
                  name: "Details",
                  selector: (row) => row.name + " - custom",
                  ui: (row) => (
                    <div className="tw-flex tw-gap-1">
                      <Link
                        className="tw-text-primary"
                        to={`/profile/${row.id}`}
                      >
                        <EyeDetailsIcon className="tw-w-8" />
                      </Link>
                    </div>
                  ),
                },
                {
                  name: "Edit",
                  selector: (row) => row.name + " - custom",
                  ui: (row) => (
                    <div className="tw-flex tw-gap-1">
                      {currentUserType !== USER_TYPES.PARTNER && (
                        <Link
                          className="tw-text-primary"
                          to={`/update-profile/${row.id}`}
                        >
                          <EditIcon className="tw-w-6" />
                        </Link>
                      )}
                    </div>
                  ),
                },
              ]
            : []),
        ]
      : [];

  const columns = useMemo(() => {
    return createColumns(
      adminPartnersData,
      currentTab === "uncompleted" ? uncompletedTab : customColumns,
      hiddenColumns,
      true
    ).map((col) => ({
      ...col,
      // sortable: col.name === "Name" || col.name === "Joined Date",
      sortable: false,
      width:
        col.name === "Num" ? "60px" : col.name === "Name" ? "150px" : "1fr",
    }));
  }, [adminPartnersData]);

  return (
    <>
      <PageTitle title="Partners" />

      <div className="bg-white flex flex-col mt-4">
        <Tabs
          options={DATA_TABS}
          onTabClick={handleTabClick}
          currentTab={currentTab}
        />

        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <div className="relative">
            <div className="tw-mb-2">
              <input
                type="text"
                onChange={handleFilter}
                value={searchTerm}
                className="border border-blue-300 rounded-lg p-2 col-md-12 tw-mb-2"
                placeholder="Search by name"
              />
            </div>
            {currentTab == "all" && (
              <div className="tw-grid xl:tw-grid-cols-5 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-2 tw-grid-cols-1 tw-items-end tw-mb-4">
                <DropdownSelect
                  options={CLIENTS_CATEGORIES}
                  placeholder={"Partner type"}
                  labelKey="name_en"
                  currentOption={filterByClientCategory}
                  setCurrentOption={setFilterByClientCategory}
                />
                <DropdownSelect
                  options={[
                    { title: "Active", id: 1 },
                    { title: "Not Active", id: 0 },
                  ]}
                  placeholder={"Activation Status"}
                  labelKey="title"
                  currentOption={filterByActivationStatus}
                  setCurrentOption={setFilterByActivationStatus}
                />
                <DropdownSelect
                  options={[
                    { title: "Yes", id: 1 },
                    { title: "No", id: 0 },
                  ]}
                  placeholder={"PV Activation"}
                  labelKey="title"
                  currentOption={filterByPvActive}
                  setCurrentOption={setFilterByPvActive}
                />

                <div className="tw-flex tw-items-center">
                  <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28 tw-pe-2">
                    Joined From:
                  </span>
                  <Flatpickr
                    value={startDate}
                    onChange={([date]) => setStartDate(date)}
                    className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
                  />
                </div>
                <div className="tw-flex tw-items-center">
                  <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28 tw-pe-2">
                    Joined To:
                  </span>
                  <Flatpickr
                    value={endDate}
                    onChange={([date]) => setEndDate(date)}
                    className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
                  />
                </div>
              </div>
            )}
          </div>
          <DataTable
            columns={columns}
            data={filterAndSortData()}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />
          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={pagination?.last_page}
              currentPage={pagination?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPartnersList;
